import { Box } from "@mui/material";
import { darken } from "@mui/material/styles";
import { onboardingTokens } from "../theme";

/**
 * A selectable pill. The shape the design uses wherever a member picks from a
 * short vocabulary — goal sub-flows, demographics.
 *
 * A real `<button>` with `aria-pressed` rather than a Chip: a clickable Chip
 * renders a `<div>`, carries no pressed state, and truncates its label to one
 * line, which loses options like "Another identity / Self-describe".
 *
 * @param {object} props
 * @param {boolean} [props.selected]
 * @param {boolean} [props.disabled]
 * @param {string} [props.fill]  unselected background. Defaults to the card
 *   colour, for pills on the page background; pills inside a white card pass
 *   `lightTeal` instead, so they separate from what is behind them.
 * @param {() => void} props.onClick
 */
const Pill = ({
  selected = false,
  disabled = false,
  fill = onboardingTokens.card,
  onClick,
  children,
  ...rest
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    disabled={disabled}
    sx={(theme) => ({
      px: 1.75,
      py: 1,
      borderRadius: 999,
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "default" : "pointer",
      fontSize: "0.8125rem",
      fontWeight: 500,
      fontFamily: onboardingTokens.fontBody,
      textAlign: "left",
      color: selected ? "primary.contrastText" : onboardingTokens.navy,
      backgroundColor: selected ? onboardingTokens.teal : fill,
      border: `1.5px solid ${
        selected ? onboardingTokens.teal : onboardingTokens.track
      }`,
      transition: theme.transitions.create(
        ["background-color", "border-color"],
        { duration: theme.transitions.duration.shortest },
      ),
      // Guarded, or a tap leaves the hover state stuck on until the next tap
      // elsewhere. `:not(:disabled)` because CSS still matches :hover on a
      // disabled button.
      "@media (hover: hover)": {
        "&:hover:not(:disabled)": {
          backgroundColor: selected
            ? theme.palette.primary.dark
            : darken(fill, 0.04),
          borderColor: selected
            ? theme.palette.primary.dark
            : onboardingTokens.mutedForeground,
        },
      },
      "&:focus-visible": {
        outline: `2px solid ${onboardingTokens.teal}`,
        outlineOffset: 2,
      },
    })}
    {...rest}
  >
    {children}
  </Box>
);

export default Pill;
